import React from 'react'
import { Box, CircularProgress } from '@mui/material'
import { BaseSelect as Select } from 'src/Libs/inputs/react-hook-form/Select'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import useAccordionStyle from 'src/Libs/styles'
import { SettingsBox } from 'src/Elements/Data'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { InstallationConfigDto } from '../../../installation/api'
import { useSolarConsumptionPriority } from '../hooks/use-solar-consumption-priority'
import { useSystemSettingsTitleAndBreadcrumbs } from 'src/Libs/system-settings'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

export type ChargingPriorityType = 'Wallbox First' | 'Storage first'

interface PrioritySettingsBoxProps {
  title: string
  options: Array<{ label: string; value: string }>
  value: ChargingPriorityType
  onChange: (value: ChargingPriorityType) => void
  disabled: boolean
}

interface SolarConsumptionPriorityPageProps {
  installationId: number
  installationConfig: Nullable<InstallationConfigDto>
  queryIsLoading: boolean
  hasWallbox: boolean
  hasStorage: boolean
}

/** User-level settings page to change solar consumption priority */
export const SolarConsumptionPriorityPage: React.FC<SolarConsumptionPriorityPageProps> = ({
  installationId,
  installationConfig,
  queryIsLoading,
  hasWallbox,
  hasStorage,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useAccordionStyle()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const { triggerAppRating } = useAppRatingContext()

  useSystemSettingsTitleAndBreadcrumbs({ pageTitleKey: 'framework.end-user.solar' })

  const { chargingPriority, wallboxOption, storageOption, updateChargingPriority, isLoading } = useSolarConsumptionPriority({
    installationId,
    installationConfig,
    queryIsLoading,
  })

  const getPrioritySettingsOptions = (): Array<{ label: string; value: string }> => {
    if (hasWallbox && !hasStorage) {
      return [wallboxOption]
    }
    if (hasStorage && !hasWallbox) {
      return [storageOption]
    }
    if (hasStorage && hasWallbox) {
      return [wallboxOption, storageOption]
    }
    return []
  }

  const priorityOptions = getPrioritySettingsOptions()

  return (
    <>
      <BannerQueue location={BannerLocations.FlowSettingsSolarPriority} sx={{ mb: 2, mt: [2, 0] }} />
      <AccordionItem
        title={t('user-settings.solar-page.settings-title')}
        expanded
        accordionDetailsSx={{ px: 4, py: 0 }}
        className={classes.accordion}
      >
        <SettingsBox
          title="user-settings.solar-page.options.priority-1"
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
              <ShellTextField label={t('user-settings.solar-page.options.house-consumption')} disabled />
            </Box>
          }
        />
        {priorityOptions.length > 0 && (
          <PrioritySettingsBox
            title="user-settings.solar-page.options.priority-2"
            options={priorityOptions}
            value={chargingPriority}
            onChange={newValue => {
              updateChargingPriority(newValue)
              triggerAppRating()
            }}
            disabled={isLoading || !userCanEdit}
          />
        )}
        {hasStorage && hasWallbox && (
          <SettingsBox
            title="user-settings.solar-page.options.priority-3"
            suffix={
              <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                <Select
                  fullWidth
                  options={priorityOptions}
                  value={chargingPriority === 'Wallbox First' ? 'Storage first' : 'Wallbox First'}
                  onChange={e => {
                    const selected = e.target.value as ChargingPriorityType
                    updateChargingPriority(selected === 'Wallbox First' ? 'Storage first' : 'Wallbox First')
                    triggerAppRating()
                  }}
                  disabled={isLoading || !userCanEdit}
                />
              </Box>
            }
          />
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </AccordionItem>
    </>
  )
}

const PrioritySettingsBox: React.FC<PrioritySettingsBoxProps> = ({ title, options, value, onChange, disabled }) => (
  <SettingsBox
    title={title}
    suffix={
      <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
        <Select fullWidth options={options} value={value} onChange={e => onChange(e.target.value as ChargingPriorityType)} disabled={disabled} />
      </Box>
    }
  />
)
